/**
 * Two small decisions about re-reading the Grow account, kept apart from the
 * provider so they can be tested without React or Firebase (B54).
 *
 * @module accountRead
 */

/**
 * Whether the identity an account read was started for is still the one
 * signed in. Under mock auth Firebase never signs in (auth.currentUser is
 * always null), so the mock uid itself stands for "current"; a real sign-in
 * is compared with Firebase's own current user, exactly as before.
 *
 * @param mock - Whether VITE_USE_MOCK_AUTH is on.
 * @param uid - The identity the read was started for.
 * @param firebaseUid - Firebase's current user's uid, if anyone is signed in.
 * @returns True when the answer should still be used.
 */
export function isIdentityCurrent(mock: boolean, uid: string, firebaseUid: string | null | undefined): boolean {
  return mock || firebaseUid === uid;
}

/**
 * What re-reading the Grow account found: an account ('ok'), no account yet
 * ('none': the server answered and there is none), or the read did not
 * succeed ('failed': a 500, 401, final 429, no connection, a suspended
 * account; the read has already reported it or said so on the sign-in page).
 */
export type AccountRead = 'ok' | 'none' | 'failed';

/**
 * What the sign-up screen should do once the account has been made and
 * re-read: carry on when an account came back (Layout swaps the screen for
 * the app); report and show the failure state when the server says there is
 * none; do nothing more when the read itself failed, because it has already
 * reported that and Layout shows the failure page (or the sign-in page, for a
 * suspended account). So one failure is one Sentry event (C78).
 *
 * @param read - What refreshMysqlUser returned.
 * @returns 'done', 'report' (none came back) or 'handled' (the read failed and said so).
 */
export function signupOutcome(read: AccountRead): 'done' | 'report' | 'handled' {
  if (read === 'ok') return 'done';
  return read === 'none' ? 'report' : 'handled';
}
